import React, { useEffect, useState } from "react";
import {
	Alert,
	Button,
	Col,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	UncontrolledTooltip,
} from "reactstrap";
import {
	normalizeShopDomain,
	saveShopifyIntegration,
	startShopifyOAuth,
	testShopifyConnection,
} from "../../../services/shopifyIntegrationApi";

const PENDING_KEY = "shopify_oauth_pending";

function normalizeSiteUrl(value) {
	const trimmed = (value || "").trim();
	if (!trimmed) return "";
	if (/^https?:\/\//i.test(trimmed)) return trimmed;
	return `https://${trimmed}`;
}

function isValidHttpUrl(value) {
	try {
		const parsed = new URL(value);
		return parsed.protocol === "http:" || parsed.protocol === "https:";
	} catch {
		return false;
	}
}

function readPendingOAuth() {
	try {
		const raw = sessionStorage.getItem(PENDING_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function writePendingOAuth(payload) {
	try {
		sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
	} catch {
		/* ignore */
	}
}

function clearPendingOAuth() {
	try {
		sessionStorage.removeItem(PENDING_KEY);
	} catch {
		/* ignore */
	}
}

export default function ShopifyIntegrationModal({
	isOpen,
	onClose,
	websiteId,
	siteUrl: initialSiteUrl = "",
	shopDomain: initialShopDomain = "",
	connected = false,
	oauthJustReturned = false,
	onSaved,
}) {
	const [shopDomain, setShopDomain] = useState("");
	const [oauthDone, setOauthDone] = useState(false);
	const [saved, setSaved] = useState(false);
	const [connectLoading, setConnectLoading] = useState(false);
	const [saveLoading, setSaveLoading] = useState(false);
	const [testLoading, setTestLoading] = useState(false);
	const [testConnectionFailed, setTestConnectionFailed] = useState(false);
	const [feedback, setFeedback] = useState(null);

	useEffect(() => {
		if (!isOpen) return;

		const pending = readPendingOAuth();
		const pendingMatches =
			pending && String(pending.websiteId) === String(websiteId) && Boolean(pending.shopDomain);
		// Pending only restores form fields after a redirect attempt.
		// "Authorized" requires a real OAuth return (?connected=shopify) or an already-saved connection.
		const authorized = Boolean(connected) || Boolean(oauthJustReturned);

		const nextShop =
			normalizeShopDomain(
				(pendingMatches && pending.shopDomain) || initialShopDomain || ""
			) || "";
		setShopDomain(nextShop);
		setOauthDone(authorized);
		setSaved(Boolean(connected));
		setTestConnectionFailed(false);
		setFeedback(
			oauthJustReturned
				? {
						type: "success",
						message:
							"Shopify authorization complete. Save the integration, then test the connection.",
					}
				: null
		);
		setConnectLoading(false);
		setSaveLoading(false);
		setTestLoading(false);
	}, [
		isOpen,
		websiteId,
		initialSiteUrl,
		initialShopDomain,
		connected,
		oauthJustReturned,
	]);

	function resolveSiteUrl() {
		return normalizeSiteUrl(initialSiteUrl);
	}

	function validateFields({ requireSiteUrl = true } = {}) {
		const shop = normalizeShopDomain(shopDomain);
		const url = resolveSiteUrl();
		if (!shop) {
			return { ok: false, message: "Shop domain is required (e.g. my-store.myshopify.com)." };
		}
		if (!shop.includes(".")) {
			return {
				ok: false,
				message: "Enter a full shop domain like my-store.myshopify.com.",
			};
		}
		if (requireSiteUrl) {
			if (!url) {
				return { ok: false, message: "Store URL is not configured for this website." };
			}
			if (!isValidHttpUrl(url)) {
				return {
					ok: false,
					message: "Enter a valid storefront URL (e.g. https://www.mystore.com).",
				};
			}
		}
		if (websiteId == null || websiteId === "") {
			return { ok: false, message: "No website selected." };
		}
		return { ok: true, shopDomain: shop, siteUrl: url };
	}

	async function handleConnect(e) {
		e?.preventDefault?.();
		const validated = validateFields({ requireSiteUrl: false });
		if (!validated.ok) {
			setFeedback({ type: "danger", message: validated.message });
			return;
		}
		setConnectLoading(true);
		setFeedback(null);
		try {
			const result = await startShopifyOAuth({
				websiteId,
				shopDomain: validated.shopDomain,
			});
			if (!result.ok || !result.data?.installUrl) {
				setFeedback({
					type: "danger",
					message: result.message || "Could not start Shopify OAuth",
				});
				return;
			}
			writePendingOAuth({
				websiteId: String(websiteId),
				shopDomain: result.data.shopDomain || validated.shopDomain,
			});
			window.location.href = result.data.installUrl;
		} catch {
			setFeedback({ type: "danger", message: "Could not start Shopify OAuth" });
		} finally {
			setConnectLoading(false);
		}
	}

	async function handleSave(e) {
		e?.preventDefault?.();
		const validated = validateFields({ requireSiteUrl: true });
		if (!validated.ok) {
			setFeedback({ type: "danger", message: validated.message });
			return;
		}
		if (!oauthDone && !connected) {
			setFeedback({
				type: "warning",
				message: "Connect Shopify first (authorize the app), then save.",
			});
			return;
		}
		setSaveLoading(true);
		setFeedback(null);
		try {
			const result = await saveShopifyIntegration({
				websiteId,
				shopDomain: validated.shopDomain,
				siteUrl: validated.siteUrl,
			});
			if (!result.ok) {
				setFeedback({
					type: "danger",
					message: result.message || "Could not save Shopify integration",
				});
				return;
			}
			clearPendingOAuth();
			setShopDomain(validated.shopDomain);
			setSaved(true);
			setOauthDone(true);
			setTestConnectionFailed(false);
			setFeedback({
				type: "success",
				message: result.message || "Shopify integration saved. You can test the connection.",
			});
			onSaved?.({
				shopDomain:
					result.data?.shop_domain ||
					result.data?.shopDomain ||
					validated.shopDomain,
				siteUrl:
					result.data?.site_url || result.data?.siteUrl || validated.siteUrl,
				integrationId: result.data?.id ?? null,
			});
		} catch {
			setFeedback({ type: "danger", message: "Could not save Shopify integration" });
		} finally {
			setSaveLoading(false);
		}
	}

	async function handleTest(e) {
		e?.preventDefault?.();
		if (!saved && !connected) {
			setFeedback({
				type: "warning",
				message: "Save the integration before testing the connection.",
			});
			return;
		}
		if (websiteId == null || websiteId === "") {
			setFeedback({ type: "danger", message: "No website selected." });
			return;
		}
		setTestLoading(true);
		setFeedback(null);
		try {
			const result = await testShopifyConnection(websiteId);
			if (!result.ok) {
				setTestConnectionFailed(true);
				setFeedback({
					type: "danger",
					message: result.message || "Shopify connection test failed",
				});
				return;
			}
			setTestConnectionFailed(false);
			setFeedback({
				type: "success",
				message: result.message || "Shopify connection successful",
			});
		} catch {
			setTestConnectionFailed(true);
			setFeedback({ type: "danger", message: "Shopify connection test failed" });
		} finally {
			setTestLoading(false);
		}
	}

	const busy = connectLoading || saveLoading || testLoading;
	const canSave = oauthDone || connected;
	const canTest = saved || connected;
	const showConnectButton = !connected || testConnectionFailed;
	const connectButtonLabel = connectLoading
		? "Connecting…"
		: testConnectionFailed
			? "Reconnect"
			: "Connect Shopify";

	function statusAlert() {
		if (feedback) {
			return (
				<Alert color={feedback.type} className="mb-0 py-2 small">
					{feedback.message}
				</Alert>
			);
		}
		if (connected || saved) {
			return (
				<Alert color="success" className="mb-0 py-2 small">
					Connected - you can re-save details or test the connection.
				</Alert>
			);
		}
		if (oauthDone) {
			return (
				<Alert color="info" className="mb-0 py-2 small">
					Authorized - save the integration, then test the connection.
				</Alert>
			);
		}
		const pending = readPendingOAuth();
		const hasPending =
			pending &&
			String(pending.websiteId) === String(websiteId) &&
			Boolean(pending.shopDomain);
		if (hasPending) {
			return (
				<Alert color="warning" className="mb-0 py-2 small">
					Previous Connect did not finish. Click Connect Shopify again to authorize, then Save.
				</Alert>
			);
		}
		return (
			<Alert color="light" className="mb-0 py-2 small text-dark border">
				Not configured - enter your shop domain, connect Shopify, then save and test.
			</Alert>
		);
	}

	return (
		<Modal
			isOpen={isOpen}
			toggle={busy ? undefined : onClose}
			size="lg"
			scrollable
			className="integration-modal"
			contentClassName="border-0 rounded"
			backdrop={busy ? "static" : true}
			keyboard={!busy}
		>
			<ModalHeader toggle={busy ? undefined : onClose} className="border-bottom">
				Shopify
			</ModalHeader>
			<ModalBody
				className="pt-4 px-4"
				style={{ maxHeight: "min(75vh, 640px)", overflowY: "auto", flexDirection: "column" }}
			>
				<p className="text-muted small mb-4 lh-base">
					Connect a Shopify store so this website can publish and sync content securely. Flow:
					Connect → Save → Test.
				</p>

				<section className="mb-4 pb-4 border-bottom">
					<div
						className="small fw-semibold text-uppercase text-muted mb-2"
						style={{ letterSpacing: "0.04em" }}
					>
						Status
					</div>
					{statusAlert()}
				</section>

				<section>
					<div
						className="small fw-semibold text-uppercase text-muted mb-2"
						style={{ letterSpacing: "0.04em" }}
					>
						Shopify store
					</div>
					<p className="text-muted small mb-3">
						<span id="shopify-help-modal">
							Enter your admin shop domain (ends with .myshopify.com). The storefront URL is taken
							from your store settings automatically.
						</span>
						<UncontrolledTooltip placement="right" target="shopify-help-modal">
							Example shop domain: my-store.myshopify.com. Access tokens stay on the server.
						</UncontrolledTooltip>
					</p>
					<form id="integration-shopify-form" onSubmit={handleSave}>
						<Row className="g-3">
							<Col xs={12}>
								<Label for="shopify-modal-shop-domain" className="small fw-semibold mb-1 d-block">
									Shop domain
								</Label>
								<Input
									id="shopify-modal-shop-domain"
									type="text"
									className="w-100"
									value={shopDomain}
									onChange={(e) => {
										setShopDomain(e.target.value);
										setOauthDone(false);
										setSaved(false);
										setTestConnectionFailed(false);
										if (feedback?.type === "success") setFeedback(null);
									}}
									placeholder="my-store.myshopify.com"
									autoComplete="off"
									disabled={busy}
								/>
							</Col>
						</Row>
					</form>
				</section>
			</ModalBody>
			<ModalFooter className="border-top px-4 py-3 bg-light gap-2 flex-wrap">
				<Button color="secondary" outline type="button" onClick={onClose} disabled={busy}>
					Cancel
				</Button>
				{showConnectButton && (
					<Button color="outline-primary" type="button" onClick={handleConnect} disabled={busy}>
						{connectButtonLabel}
					</Button>
				)}
				<Button
					color="primary"
					type="submit"
					form="integration-shopify-form"
					disabled={busy || !canSave}
				>
					{saveLoading ? "Saving…" : "Save integration"}
				</Button>
				<Button
					color="outline-success"
					type="button"
					onClick={handleTest}
					disabled={busy || !canTest}
				>
					{testLoading ? "Testing…" : "Test connection"}
				</Button>
			</ModalFooter>
		</Modal>
	);
}
